import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { parseCardTopUps } from "@/hooks/queries/usePublicConfig";
import type { TxFilter, WalletSummary, WalletTransaction } from "@/lib/wallet";

// Prepaid wallet data hooks (/api/wallet). Every mutation that moves money
// invalidates the whole ["wallet"] tree so balance and ledger stay in sync.

export const WALLET_KEY = ["wallet"] as const;

const toNum = (v: unknown, fallback = 0) => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) ? n : fallback;
};

export function useWallet(enabled = true) {
  return useQuery({
    queryKey: [...WALLET_KEY, "summary"],
    enabled,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<WalletSummary> => {
      const r = await api.get("/wallet");
      return {
        balance: toNum(r.balance),
        currency: r.currency || "USD",
        lowBalanceThreshold: toNum(r.lowBalanceThreshold, 1),
        lowBalance: !!r.lowBalance,
        monthSpend: toNum(r.monthSpend),
        cardTopUps: parseCardTopUps(r.cardTopUps),
      };
    },
  });
}

export function useWalletTransactions(params: { page: number; limit: number; type: TxFilter }) {
  return useQuery({
    queryKey: [...WALLET_KEY, "transactions", params],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const qs = new URLSearchParams({ page: String(params.page), limit: String(params.limit), type: params.type });
      const r = await api.get(`/wallet/transactions?${qs}`);
      const rows = (Array.isArray(r.data) ? r.data : []).map(
        (t: WalletTransaction): WalletTransaction => ({
          ...t,
          id: String(t.id),
          amount: toNum(t.amount),
          balanceAfter: toNum(t.balanceAfter),
        })
      );
      return { rows, total: toNum(r.pagination?.total) };
    },
  });
}

function useInvalidateWallet() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: WALLET_KEY });
    // Credit changes can raise/clear low-balance notifications.
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };
}

export function useRedeemCoupon() {
  const invalidate = useInvalidateWallet();
  return useMutation({
    mutationFn: async (code: string): Promise<{ amount: number; balance: number; code: string }> => {
      const r = await api.post("/wallet/redeem", { code });
      return { amount: toNum(r.amount), balance: toNum(r.balance), code: r.code };
    },
    onSuccess: invalidate,
  });
}

export function useWalletSettings() {
  const invalidate = useInvalidateWallet();
  return useMutation({
    mutationFn: (lowBalanceThreshold: number) => api.put("/wallet/settings", { lowBalanceThreshold }),
    onSuccess: invalidate,
  });
}

/** Starts a Stripe Checkout session; resolves to the hosted checkout URL. */
export function useCardTopUp() {
  return useMutation({
    mutationFn: async (amount: number): Promise<string> => {
      const r = await api.post("/payments/wallet-top-up", { amount });
      if (!r.url) throw new Error("Could not start card payment");
      return r.url as string;
    },
  });
}

// ------------------------------------------------------------------ admin

export type CouponStatus = "active" | "inactive" | "expired" | "exhausted";

export interface Coupon {
  id: string;
  code: string;
  amount: number;
  maxRedemptions: number | null;
  redemptionCount: number;
  perUserLimit: number;
  expiresAt: string | null;
  isActive: boolean;
  status: CouponStatus | string;
  description: string | null;
  batchId: string | null;
  createdBy: { id: number; username: string } | null;
  createdAt: string;
}

export interface CouponRedemption {
  id: string;
  amount: number;
  user: { id: number; username: string; email: string } | null;
  createdAt: string;
}

export interface CreateCouponsInput {
  amount: number;
  quantity: number;
  code?: string;
  maxRedemptions: number | null;
  perUserLimit: number;
  expiresAt?: string | null;
  description?: string;
}

const COUPONS_KEY = [...WALLET_KEY, "admin", "coupons"] as const;

export function useAdminCoupons(params: { page: number; limit: number; search: string; status: "all" | CouponStatus }) {
  return useQuery({
    queryKey: [...COUPONS_KEY, params],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const qs = new URLSearchParams({
        page: String(params.page),
        limit: String(params.limit),
        status: params.status,
        ...(params.search ? { search: params.search } : {}),
      });
      const r = await api.get(`/wallet/admin/coupons?${qs}`);
      const rows = (Array.isArray(r.data) ? r.data : []).map(
        (c: Coupon): Coupon => ({
          ...c,
          id: String(c.id),
          amount: toNum(c.amount),
          redemptionCount: toNum(c.redemptionCount),
        })
      );
      return { rows, total: toNum(r.pagination?.total) };
    },
  });
}

export function useCreateCoupons() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateCouponsInput): Promise<{ batchId: string; coupons: { id: string; code: string }[] }> => {
      const r = await api.post("/wallet/admin/coupons", input);
      return { batchId: r.batchId, coupons: Array.isArray(r.coupons) ? r.coupons : [] };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: COUPONS_KEY }),
  });
}

export function useSetCouponActive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch(`/wallet/admin/coupons/${encodeURIComponent(id)}`, { isActive }),
    onSuccess: () => qc.invalidateQueries({ queryKey: COUPONS_KEY }),
  });
}

export function useCouponRedemptions(id: string | null) {
  return useQuery({
    queryKey: [...COUPONS_KEY, "redemptions", id],
    enabled: !!id,
    queryFn: async (): Promise<CouponRedemption[]> => {
      const r = await api.get(`/wallet/admin/coupons/${encodeURIComponent(id!)}/redemptions`);
      return (Array.isArray(r.redemptions) ? r.redemptions : []).map((x: CouponRedemption) => ({
        ...x,
        amount: toNum(x.amount),
      }));
    },
  });
}

export function useGrantCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { userId: number; amount: number; note: string }): Promise<{ balance: number }> => {
      const r = await api.post("/wallet/admin/credit", input);
      return { balance: toNum(r.balance) };
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: WALLET_KEY }),
  });
}
